/**
 * Host Global - Property Rental Management System
 * Footer Component
 */

import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import typography from "assets/theme/base/typography";
import { contactConfig } from "config/contact";

const BRAND_DARK = "#272e4e";
const BRAND_ACCENT = "#b6925b";

function Footer({ company, links }) {
  const { size } = typography;
  const { supportEmail, supportPhone } = contactConfig;

  const renderLinks = () =>
    links &&
    links.length > 0 &&
    links.map((link) => (
      <MDBox key={link.name} component="li" px={1.5} lineHeight={1}>
        <MDTypography
          component="a"
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          variant="button"
          fontWeight="regular"
          color="text"
          sx={{ "&:hover": { color: BRAND_ACCENT } }}
        >
          {link.name}
        </MDTypography>
      </MDBox>
    ));

  return (
    <MDBox
      component="footer"
      width="100%"
      py={3}
      px={2}
      mt="auto"
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: ({ palette }) =>
          palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "transparent",
      }}
    >
      <MDBox
        maxWidth="1200px"
        mx="auto"
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "center", md: "center" }}
        gap={3}
        flexWrap="wrap"
      >
        {/* Brand & Copyright */}
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems={{ xs: "center", md: "flex-start" }}
          gap={0.5}
        >
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ color: BRAND_DARK, letterSpacing: "0.5px" }}
          >
            Hosta Global
          </MDTypography>
          <MDTypography variant="caption" color="text" fontSize={size.sm}>
            &copy; {new Date().getFullYear()} Hosta Global. All rights reserved.
          </MDTypography>
        </MDBox>

        {/* Contact */}
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems="center"
          gap={{ xs: 1.5, sm: 3 }}
          flexWrap="wrap"
          justifyContent="center"
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: "1.1rem", color: BRAND_ACCENT }}>email</Icon>
            <MDTypography
              component="a"
              href={`mailto:${supportEmail}`}
              variant="button"
              fontWeight="regular"
              color="text"
              fontSize={size.sm}
              sx={{
                textDecoration: "none",
                "&:hover": { color: BRAND_ACCENT, textDecoration: "underline" },
              }}
            >
              {supportEmail}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: "1.1rem", color: BRAND_ACCENT }}>phone</Icon>
            <MDTypography
              component="a"
              href={`tel:${supportPhone.replace(/\s/g, "")}`}
              variant="button"
              fontWeight="regular"
              color="text"
              fontSize={size.sm}
              sx={{
                textDecoration: "none",
                "&:hover": { color: BRAND_ACCENT, textDecoration: "underline" },
              }}
            >
              {supportPhone}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Optional links */}
        {links && links.length > 0 && (
          <MDBox
            component="ul"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              listStyle: "none",
              m: 0,
              p: 0,
              gap: 0,
            }}
          >
            {renderLinks()}
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

Footer.defaultProps = {
  company: { href: "#", name: "Host Global" },
  links: [],
};

Footer.propTypes = {
  company: PropTypes.objectOf(PropTypes.string),
  links: PropTypes.arrayOf(PropTypes.object),
};

export default Footer;
